import type { OverlayDoc } from "~/ui-rtk/api/adForgeApi"
import {
  ensureFontsLoaded,
  fontFamiliesInDoc,
  renderOverlay,
  type RenderAssets,
} from "./render-overlay"

/**
 * Client-side compositor: bakes the overlay document onto the base media.
 * Images → PNG via a single canvas draw. Videos → re-recorded through
 * canvas.captureStream + MediaRecorder (same technique as KenBurnsPlayer),
 * with the source audio piped through WebAudio so it survives the muted
 * playback. All media sources MUST be taint-free (data: URLs from the
 * proxy-image / proxy-video endpoints) or toBlob/captureStream will throw.
 */

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error("Failed to load image"))
    img.src = src
  })
}

/** data: URL → Blob without fetch() — fetch(data:) is blocked by the prod CSP. */
export function dataUrlToBlob(dataUrl: string): Blob {
  const [meta, payload] = dataUrl.split(",")
  const mime = meta.match(/^data:([^;,]+)/)?.[1] ?? "application/octet-stream"
  const binary = atob(payload)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

/**
 * Center-crop cover draw: fills the canvas, cropping overflow — matches the
 * editor's `object-cover` media so exports stay WYSIWYG when the canvas
 * ratio differs from the media's.
 */
function drawCover(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
): void {
  const cw = ctx.canvas.width
  const ch = ctx.canvas.height
  const scale = Math.max(cw / (sourceWidth || 1), ch / (sourceHeight || 1))
  const dw = sourceWidth * scale
  const dh = sourceHeight * scale
  ctx.drawImage(source, (cw - dw) / 2, (ch - dh) / 2, dw, dh)
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  // Give the browser a beat to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

export interface ExportImageParams {
  /** Taint-free source (data: URL from proxy-image). */
  baseSrc: string
  doc: OverlayDoc
  assets?: RenderAssets
  filename?: string
}

export async function exportImageWithOverlay({
  baseSrc,
  doc,
  assets,
  filename = `brand-overlay-${Date.now()}.png`,
}: ExportImageParams): Promise<File> {
  await ensureFontsLoaded(fontFamiliesInDoc(doc))
  const baseImage = await loadImage(baseSrc)

  const canvas = document.createElement("canvas")
  canvas.width = doc.canvas.width
  canvas.height = doc.canvas.height
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("Canvas 2D context unavailable")

  drawCover(ctx, baseImage, baseImage.naturalWidth, baseImage.naturalHeight)
  renderOverlay(ctx, doc, 1, assets)

  const blob = await new Promise<Blob | null>(resolve =>
    canvas.toBlob(resolve, "image/png"),
  )
  if (!blob) throw new Error("Failed to encode composited image")
  return new File([blob], filename, { type: "image/png" })
}

export interface ExportVideoParams {
  /** Taint-free source (data: URL from proxy-video). */
  videoSrc: string
  doc: OverlayDoc
  assets?: RenderAssets
  filename?: string
  /** 0..1 based on playback position. */
  onProgress?: (fraction: number) => void
}

export async function exportVideoWithOverlay({
  videoSrc,
  doc,
  assets,
  filename,
  onProgress,
}: ExportVideoParams): Promise<File> {
  await ensureFontsLoaded(fontFamiliesInDoc(doc))

  const video = document.createElement("video")
  video.src = videoSrc
  video.crossOrigin = "anonymous"
  video.playsInline = true
  video.muted = true // required for programmatic play(); audio is captured via WebAudio below

  await new Promise<void>((resolve, reject) => {
    video.onloadedmetadata = () => resolve()
    video.onerror = () => reject(new Error("Failed to load video"))
  })

  const canvas = document.createElement("canvas")
  canvas.width = doc.canvas.width
  canvas.height = doc.canvas.height
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("Canvas 2D context unavailable")

  const stream = canvas.captureStream(30)

  // Route the video's audio into the recording without audible playback:
  // an element source disconnects the element from the speakers, and we only
  // connect it to the MediaStream destination. Best-effort — a video with no
  // audio track (or a WebAudio failure) still exports fine, just silent.
  let audioContext: AudioContext | null = null
  try {
    audioContext = new AudioContext()
    const source = audioContext.createMediaElementSource(video)
    const destination = audioContext.createMediaStreamDestination()
    source.connect(destination)
    for (const track of destination.stream.getAudioTracks()) {
      stream.addTrack(track)
    }
    video.muted = false
  } catch {
    audioContext = null
  }

  // Same mime pick as KenBurnsPlayer — mp4/avc1 (Safari, some Chrome builds)
  // with a webm fallback.
  const mimeType = MediaRecorder.isTypeSupported("video/mp4;codecs=avc1")
    ? "video/mp4;codecs=avc1"
    : "video/webm"
  const recorder = new MediaRecorder(stream, {
    mimeType,
    videoBitsPerSecond: 8_000_000,
  })
  const chunks: Blob[] = []
  recorder.ondataavailable = e => chunks.push(e.data)

  // Not in every TS dom lib yet — feature-detect through a narrow cast.
  const videoWithVfc = video as HTMLVideoElement & {
    requestVideoFrameCallback?: (callback: () => void) => number
  }

  // The overlay is static across frames — rasterize it ONCE onto a
  // transparent layer, then composite the bitmap per frame.
  const overlayLayer = document.createElement("canvas")
  overlayLayer.width = doc.canvas.width
  overlayLayer.height = doc.canvas.height
  const overlayCtx = overlayLayer.getContext("2d")
  if (!overlayCtx) throw new Error("Canvas 2D context unavailable")
  renderOverlay(overlayCtx, doc, 1, assets)

  const drawFrame = (): void => {
    drawCover(ctx, video, video.videoWidth, video.videoHeight)
    ctx.drawImage(overlayLayer, 0, 0)
    if (video.duration > 0) onProgress?.(video.currentTime / video.duration)
  }

  const result = await new Promise<Blob>((resolve, reject) => {
    let rafId = 0
    const scheduleDraw = (): void => {
      if (video.ended || video.paused) return
      drawFrame()
      if (videoWithVfc.requestVideoFrameCallback) {
        videoWithVfc.requestVideoFrameCallback(scheduleDraw)
      } else {
        rafId = requestAnimationFrame(scheduleDraw)
      }
    }

    recorder.onstop = () => {
      cancelAnimationFrame(rafId)
      resolve(new Blob(chunks, { type: recorder.mimeType }))
    }
    recorder.onerror = () => reject(new Error("Video recording failed"))
    video.onended = () => {
      // Flush the final frame before stopping so the last moment isn't cut.
      drawFrame()
      recorder.stop()
    }

    drawFrame() // first frame before playback starts
    recorder.start()
    video.play().then(scheduleDraw, () => {
      recorder.stop()
      reject(new Error("Video playback was blocked"))
    })
  }).finally(() => {
    void audioContext?.close().catch(() => undefined)
    video.src = ""
  })

  // MediaRecorder reports the full mime with codec params
  // ("video/mp4;codecs=avc1,opus") — the upload pipeline validates against
  // bare container types, so strip the parameters.
  const containerType = result.type.split(";")[0] || "video/webm"
  const extension = containerType.includes("mp4") ? "mp4" : "webm"
  return new File(
    [result],
    filename ?? `brand-overlay-${Date.now()}.${extension}`,
    { type: containerType },
  )
}
